import styled from '@emotion/styled';
import { useIntl } from 'react-intl';

import { AnyRoadLogoGrayFooter2023 } from 'views/anyguide/layout/logo/images/AnyRoadLogoGrayFooter2023';
import { colors } from 'ui_system/compass';

const Bullet = styled.span`
  color: ${colors.text.icon};
  font-size: clamp(8px, 2vw, 10px);
  margin: 0 clamp(4px, 1vw, 8px);
`;

const PoweredByContainer = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 767px) {
    margin-top: 24px;
  }

  .top-border {
    border: 0;
    border-top: 1px solid #e6e6e6;
    margin-bottom: clamp(12px, 3vw, 18px);
    width: clamp(50px, 15vw, 70px);
  }
`;

const Logo = styled(AnyRoadLogoGrayFooter2023)`
  width: clamp(60px, 15vw, 100px);
  max-width: 100%;
  height: auto;
  fill: #898c9a;
`;

const TermsLinksContainer = styled.div`
  margin-top: clamp(0.125rem, 0.25vw, 0.25rem);
  margin-bottom: clamp(1.25rem, 5vw, 1.125rem);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: clamp(0.25rem, 1vw, 0.5rem);
`;

const TermsLink = styled.a`
  color: #6e6e6e;
  font-size: clamp(10px, 2.5vw, 12px);
  text-decoration: underline;
  min-height: 44px;
  display: flex;
  align-items: center;

  &:focus {
    outline: 1px solid ${colors.text.link};
  }

  &:hover {
    color: ${colors.text.link};
  }
`;

interface PoweredByProps {
  hidePolicyLinks?: boolean;
}

export const PoweredBy = ({ hidePolicyLinks = false }: PoweredByProps) => {
  const { formatMessage } = useIntl();
  const link = (
    linkDestination: string,
    linkText: string,
    ariaLabel?: string
  ) => {
    return (
      <TermsLink
        className="terms-link"
        target="_blank"
        rel="noopener noreferrer"
        href={linkDestination}
        aria-label={ariaLabel}
      >
        {linkText}
      </TermsLink>
    );
  };

  return (
    <PoweredByContainer
      className="powered-by-container"
      data-testid="powered-by"
    >
      <a
        aria-label="Anyroad logo - link to Anyroad.com homepage"
        className="logo-link"
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.anyroad.com/"
      >
        <Logo />
      </a>
      {!hidePolicyLinks && (
        <TermsLinksContainer>
          {link(
            'https://www.anyroad.com/end-user-terms',
            formatMessage({
              defaultMessage: 'Terms of Service',
              description: 'Link text for Terms of Service document',
            }),
            // eslint-disable-next-line quotes
            "Read Anyroad's Terms of Service - opens in new tab"
          )}
          <Bullet aria-hidden="true">●</Bullet>
          {link(
            'https://www.anyroad.com/privacy',
            formatMessage({
              defaultMessage: 'Privacy Policy',
              description: 'Link text for Privacy Policy document',
            }),
            // eslint-disable-next-line quotes
            "Read Anyroad's Privacy Policy - opens in new tab"
          )}
        </TermsLinksContainer>
      )}
    </PoweredByContainer>
  );
};
